/**
 * Starfield.js - Enhanced procedural star field generation
 *
 * Features:
 * - Varying star sizes (small, medium, large)
 * - Subtle stellar colors based on temperature (blue-white, white, yellow, orange-red)
 * - Milky Way band effect
 * - Optimized to exclude stars below ocean (southern hemisphere)
 * - Performance-conscious using BufferGeometry with vertex colors
 */

import * as THREE from 'three';

/**
 * Create a circular particle texture for smooth round stars
 * This replaces the default square particle appearance
 * @returns {THREE.CanvasTexture} Circular particle texture
 */
function createCircularParticleTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');

  // Create radial gradient for smooth circular particle
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
  gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.5)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0.0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

// Create the circular particle texture once and reuse it
const circularParticleTexture = createCircularParticleTexture();

const STARFIELD_CONFIG = {
  backgroundStars: 3000,  // Reduced so Milky Way band stands out
  milkyWayStars:   6000,  // Higher density for the band effect
  domeScale:       2600,  // World-space radius of the star sphere. Must enclose the
                          // farthest visible backdrop (Mt. Fuji's summit sits ~2400
                          // from the dome center) — otherwise that opaque geometry,
                          // being beyond the dome, lets the additive stars draw over
                          // it. Camera far plane (main.js) must stay > this + orbit.
  domeYOffset:    -320,   // Lower dome to meet water level
  basePointSize:   9.1,   // PointsMaterial base size. Scales with domeScale (size
                          // attenuates by 1/distance) so the bigger dome keeps the
                          // same on-screen star size: 7.0 * (2600/2000).
};

/**
 * Star color palettes based on stellar spectral classes
 * Values are brightened significantly for visibility against very dark skies
 * Colors are multiplied by 3-4x to work with additive blending on dark backgrounds
 */
const STAR_COLORS = {
  // Hot blue-white stars (O, B class) - ~10%
  BLUE_WHITE: [
    new THREE.Color(0xffffff).multiplyScalar(3.5),
    new THREE.Color(0xe0f0ff).multiplyScalar(3.5),
    new THREE.Color(0xd0e8ff).multiplyScalar(3.5)
  ],

  // Normal white stars (A, F class) - ~30%
  WHITE: [
    new THREE.Color(0xffffff).multiplyScalar(4.0),
    new THREE.Color(0xffffff).multiplyScalar(3.8),
    new THREE.Color(0xfff8f8).multiplyScalar(3.5)
  ],

  // Yellow-white stars (G class, like our Sun) - ~35%
  YELLOW_WHITE: [
    new THREE.Color(0xfff4e0).multiplyScalar(3.2),
    new THREE.Color(0xfff0d8).multiplyScalar(3.0),
    new THREE.Color(0xffe8c8).multiplyScalar(3.0)
  ],

  // Orange-red stars (K, M class) - ~25%
  ORANGE_RED: [
    new THREE.Color(0xffddcc).multiplyScalar(2.8),
    new THREE.Color(0xffd5b8).multiplyScalar(2.8),
    new THREE.Color(0xffc8a8).multiplyScalar(2.5)
  ]
};

/**
 * Star size distribution
 * Most stars are small, fewer are medium, very few are large
 * Sizes increased for better visibility against dark skies
 * IMPORTANT: Minimum size increased to ensure tiny stars are actually visible
 */
const STAR_SIZES = {
  TINY: { size: 2.0, probability: 0.30 },      // 30% tiny stars (increased from 1.2 for visibility)
  SMALL: { size: 2.8, probability: 0.45 },     // 45% small stars
  MEDIUM: { size: 4.0, probability: 0.18 },    // 18% medium stars
  LARGE: { size: 6.0, probability: 0.05 },     // 5% large stars
  EXTRA_LARGE: { size: 9.0, probability: 0.02 } // 2% extra-large rare stars
};

// Reusable temp objects for star generation (avoid per-star allocations)
const _starColor = new THREE.Color();
const _starVec = new THREE.Vector3();

/**
 * Get a random star color based on realistic stellar distribution.
 * Returns the palette color directly (read-only, do NOT mutate).
 */
function getRandomStarColor() {
  const rand = Math.random();

  if (rand < 0.10) {
    // 10% blue-white
    return STAR_COLORS.BLUE_WHITE[Math.floor(Math.random() * STAR_COLORS.BLUE_WHITE.length)];
  } else if (rand < 0.40) {
    // 30% white
    return STAR_COLORS.WHITE[Math.floor(Math.random() * STAR_COLORS.WHITE.length)];
  } else if (rand < 0.75) {
    // 35% yellow-white
    return STAR_COLORS.YELLOW_WHITE[Math.floor(Math.random() * STAR_COLORS.YELLOW_WHITE.length)];
  } else {
    // 25% orange-red
    return STAR_COLORS.ORANGE_RED[Math.floor(Math.random() * STAR_COLORS.ORANGE_RED.length)];
  }
}

/**
 * Get a random star size based on distribution
 */
function getRandomStarSize() {
  const rand = Math.random();
  let cumulative = 0;

  // Check each size category in order
  cumulative += STAR_SIZES.TINY.probability;
  if (rand < cumulative) return STAR_SIZES.TINY.size;

  cumulative += STAR_SIZES.SMALL.probability;
  if (rand < cumulative) return STAR_SIZES.SMALL.size;

  cumulative += STAR_SIZES.MEDIUM.probability;
  if (rand < cumulative) return STAR_SIZES.MEDIUM.size;

  cumulative += STAR_SIZES.LARGE.probability;
  if (rand < cumulative) return STAR_SIZES.LARGE.size;

  // If we get here, it's an extra-large star
  return STAR_SIZES.EXTRA_LARGE.size;
}

/**
 * Get the Milky Way density at a point on the sky sphere.
 * Returns a 0-1 value with Gaussian falloff from the band center,
 * producing soft, cloud-like edges instead of a hard cutoff.
 *
 * @param {number} phi - Spherical phi (elevation) angle
 * @param {number} theta - Spherical theta (azimuth) angle
 * @returns {number} - Density value 0-1 (0 = outside band, 1 = band center)
 */
// Pre-computed tilt constants for Milky Way band rotation
const MILKY_WAY_TILT = Math.PI * 0.3;
const TILT_COS = Math.cos(MILKY_WAY_TILT);
const TILT_SIN = Math.sin(MILKY_WAY_TILT);

function getMilkyWayDensity(phi, theta) {
  // Convert to Cartesian coordinates
  const x = Math.sin(phi) * Math.cos(theta);
  const y = Math.sin(phi) * Math.sin(theta);
  const z = Math.cos(phi);

  // Rotate around X axis to tilt the band (~55 degrees)
  const y2 = y * TILT_COS - z * TILT_SIN;
  const z2 = y * TILT_SIN + z * TILT_COS;

  // Gaussian falloff across the band width (soft edges)
  const bandWidth = 0.12;
  const widthDensity = Math.exp(-(z2 * z2) / (2 * bandWidth * bandWidth));

  // Fade out along the band so it only appears on one side of the sky.
  // bandAngle is the position along the band's length.
  const bandAngle = Math.atan2(x, y2);
  const angleCenter = -1.5; // Shift peak to one side of the sky
  const angleSpan = 1.0;
  const angleDist = bandAngle - angleCenter;
  const angleDensity = Math.exp(-(angleDist * angleDist) / (2 * angleSpan * angleSpan));

  return widthDensity * angleDensity;
}

// --- Milky Way star appearance helpers ---

// Milky Way color distribution skews bluer/whiter than background; brightness scales with density.
function getMilkyWayStarColor(density) {
  const rand = Math.random();
  if (rand < 0.15) {
    _starColor.copy(STAR_COLORS.BLUE_WHITE[Math.floor(Math.random() * STAR_COLORS.BLUE_WHITE.length)]);
  } else if (rand < 0.55) {
    _starColor.copy(STAR_COLORS.WHITE[Math.floor(Math.random() * STAR_COLORS.WHITE.length)]);
  } else if (rand < 0.80) {
    _starColor.copy(STAR_COLORS.YELLOW_WHITE[Math.floor(Math.random() * STAR_COLORS.YELLOW_WHITE.length)]);
  } else {
    _starColor.copy(STAR_COLORS.ORANGE_RED[Math.floor(Math.random() * STAR_COLORS.ORANGE_RED.length)]);
  }
  _starColor.multiplyScalar(0.3 + 0.2 * density);
  return _starColor;
}

// Milky Way stars are mostly tiny — distant, subtle cloud of light; size scales with density.
function getMilkyWayStarSize(density) {
  const r = Math.random();
  const size = r < 0.85 ? STAR_SIZES.TINY.size * 0.6
             : r < 0.95 ? STAR_SIZES.TINY.size
             : r < 0.99 ? STAR_SIZES.SMALL.size
             :             STAR_SIZES.MEDIUM.size;
  return size * (0.5 + 0.5 * density);
}

/**
 * Shared star-generation loop. Samples random (phi, theta) on the northern hemisphere,
 * gates on an optional density function, then delegates color and size to callbacks.
 * @param {number}   count             - Target star count
 * @param {number}   attemptsMultiplier - Max attempts = count × this
 * @param {Array}    positions/colors/sizes - Output arrays (mutated in place)
 * @param {Function} colorFn(density)  - Returns a THREE.Color (may be a shared temp)
 * @param {Function} sizeFn(density)   - Returns a float
 * @param {Function|null} densityFn    - If provided, called per candidate; star skipped when random > density
 */
function generateStarRegion(count, attemptsMultiplier, positions, colors, sizes, colorFn, sizeFn, densityFn = null) {
  let starsCreated = 0;
  let attempts = 0;
  const maxAttempts = count * attemptsMultiplier;

  while (starsCreated < count && attempts < maxAttempts) {
    attempts++;
    const phi = Math.acos(2 * Math.random() - 1);
    const theta = 2 * Math.PI * Math.random();

    if (phi > Math.PI * 0.5) continue; // northern hemisphere only (above ocean)

    const density = densityFn ? densityFn(phi, theta) : 1.0;
    if (densityFn && Math.random() > density) continue;

    _starVec.setFromSphericalCoords(1, phi, theta);
    positions.push(_starVec.x, _starVec.y, _starVec.z);

    const c = colorFn(density);
    colors.push(c.r, c.g, c.b);
    sizes.push(sizeFn(density));

    starsCreated++;
  }
}

/**
 * Creates an enhanced star field with varying sizes, colors, and a Milky Way band
 *
 * @returns {THREE.Points} - The star field mesh
 */
export function createStarField() {
  const positions = [];
  const colors = [];
  const sizes = [];

  generateStarRegion(STARFIELD_CONFIG.backgroundStars, 2,  positions, colors, sizes, getRandomStarColor, getRandomStarSize);
  generateStarRegion(STARFIELD_CONFIG.milkyWayStars,   30, positions, colors, sizes, getMilkyWayStarColor, getMilkyWayStarSize, getMilkyWayDensity);

  // Create BufferGeometry with vertex attributes
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));

  // Custom shader material for size variation
  const material = new THREE.PointsMaterial({
    size: STARFIELD_CONFIG.basePointSize,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0,                       // Start hidden (day/night system will control this)
    depthWrite: false,
    depthTest: true,
    vertexColors: true,               // Use vertex colors for star tinting
    blending: THREE.AdditiveBlending, // Additive blending for star glow
    map: circularParticleTexture      // Use circular texture for smooth round stars
    // No alphaTest - additive blending handles transparency naturally
  });

  // Override the onBeforeCompile to use custom size attribute
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader.replace(
      'attribute float size;',
      'attribute float size;\nvarying vec3 vColor;'
    );

    shader.vertexShader = shader.vertexShader.replace(
      '#include <color_vertex>',
      '#include <color_vertex>\nvColor = color;'
    );

    shader.vertexShader = shader.vertexShader.replace(
      'gl_PointSize = size;',
      'gl_PointSize = size * 1.2;'
    );

    shader.fragmentShader = shader.fragmentShader.replace(
      'varying vec3 vColor;',
      'varying vec3 vColor;\n'
    );

    // Add bright glow to stars with sharp center
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <premultiplied_alpha_fragment>',
      `
        // Hard circle star with early discard
        float dist = length(gl_PointCoord - vec2(0.5));
        if (dist > 0.5) discard;

        #include <premultiplied_alpha_fragment>
      `
    );
  };

  const starField = new THREE.Points(geometry, material);
  starField.scale.setScalar(STARFIELD_CONFIG.domeScale);
  starField.position.y = STARFIELD_CONFIG.domeYOffset;
  starField.renderOrder = -1; // Render first as background, everything else draws on top

  return starField;
}
